import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import Logo from '@/components/Logo';
import { ArrowRight, ShieldCheck, Camera, Award, MapPin, CheckCircle2, Clock, Phone } from 'lucide-react';
import cityData, { commonFAQs, getNearbyCities } from '@/data/cityData';
import SEO from '@/components/SEO';

export default function CityPage() {
  const navigate = useNavigate();
  const { citySlug } = useParams();
  const city = cityData[citySlug];

  // Fallback if city not found
  if (!city) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <p className="text-lg font-semibold text-[var(--brand-slate-900)] mb-4">City not found</p>
            <Button onClick={() => navigate('/')}>Return Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const nearbyCities = getNearbyCities(citySlug);

  // City-specific FAQs (combines common + local)
  const cityFAQs = [
    {
      question: `How much do impact windows cost in ${city.name}?`,
      answer: commonFAQs[0].answer
    },
    {
      question: `Do I need a permit in ${city.name}?`,
      answer: city.permitInfo
    },
    {
      question: 'How long does installation take?',
      answer: city.installTime
    },
    ...commonFAQs.slice(3) // Add remaining common FAQs
  ];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] pb-20 lg:pb-0">
      <SEO
        title={`Impact Windows in ${city.name}: Pricing, Permits & Installation`}
        description={`Impact windows and doors in ${city.name}, ${city.county} County. ${city.localContext} Get a photo-based quote — no in-home appointment required.`}
        path={`/impact-windows/${citySlug}`}
      />
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-[hsl(var(--border))]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <Logo variant="light" size="small" />
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-[var(--brand-slate-600)] text-xs hidden sm:inline-flex">
              Home
            </Button>
            <Button onClick={() => navigate('/get-quote')} data-testid={`city-nav-get-quote-${citySlug}`} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-10 px-4 text-sm font-semibold">
              Get Quote <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </nav>

      {/* ========== HERO ========== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" style={{background: 'radial-gradient(1200px circle at 20% 10%, rgba(217,238,247,0.9), rgba(246,241,232,0.65) 35%, transparent 70%)'}} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-[var(--brand-ocean-700)]" />
              <p className="text-xs uppercase tracking-widest text-[var(--brand-slate-600)] font-medium">{city.county} County, {city.state}</p>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[var(--brand-slate-900)] leading-[1.08] mb-5">
              Impact Windows &amp; Doors in {city.name}
            </h1>
            <p className="text-base sm:text-lg text-[var(--brand-slate-600)] max-w-2xl leading-relaxed mb-6">
              {city.localContext}
            </p>
            <div className="bg-[var(--brand-ocean-100)]/30 rounded-xl px-4 py-3 inline-block mb-6">
              <p className="text-sm text-[var(--brand-ocean-700)] font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4" />
                Licensed General Contractor (CGC1524851) • Fully Insured • 30+ Years Experience
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/get-quote')} size="lg" data-testid={`hero-get-quote-${citySlug}`} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-14 px-8 text-base font-semibold shadow-sm">
                Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button onClick={() => navigate('/')} size="lg" variant="outline" className="rounded-xl h-14 px-8 text-base font-semibold">
                Learn More
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICES ========== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--brand-slate-900)] text-center mb-8">
            Our Services in {city.name}
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="rounded-2xl shadow-[var(--shadow-soft)] border-2 border-[var(--brand-ocean-200)]">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-3">Impact Windows</h3>
                <ul className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Hurricane-rated protection for homes and businesses</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Energy-efficient Low-E glass reduces cooling costs</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Noise reduction for quieter indoor living</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>UV protection prevents furniture fading</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Insurance premium discounts up to 45%</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-[var(--shadow-soft)] border-2 border-[var(--brand-ocean-200)]">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-3">Impact Doors</h3>
                <ul className="space-y-2 text-sm text-[var(--brand-slate-600)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Impact-rated entry doors and sliding glass doors</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Enhanced security and break-in resistance</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Modern styles to match your home's architecture</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Weatherproof seals prevent water intrusion</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[var(--brand-ocean-700)] shrink-0 mt-0.5" />
                    <span>Code-compliant installation with permits</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== TRUST / CREDENTIALS ========== */}
      <section className="py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="rounded-2xl shadow-[var(--shadow-soft)] border-2 border-[var(--brand-ocean-200)]">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-3">
                  <ShieldCheck className="h-6 w-6 text-[var(--brand-ocean-700)]" />
                </div>
                <p className="text-sm font-semibold text-[var(--brand-slate-900)] mb-1">Licensed &amp; Insured</p>
                <p className="text-xs text-[var(--brand-slate-600)]">CGC1524851 • Liability + Workers Comp</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-[var(--shadow-soft)] border-2 border-[var(--brand-ocean-200)]">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-3">
                  <Award className="h-6 w-6 text-[var(--brand-ocean-700)]" />
                </div>
                <p className="text-sm font-semibold text-[var(--brand-slate-900)] mb-1">30+ Years Experience</p>
                <p className="text-xs text-[var(--brand-slate-600)]">Family-Owned • South Florida Specialist</p>
              </CardContent>
            </Card>

            <Card className="rounded-2xl shadow-[var(--shadow-soft)] border-2 border-[var(--brand-ocean-200)]">
              <CardContent className="p-5 text-center">
                <div className="w-12 h-12 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-3">
                  <Clock className="h-6 w-6 text-[var(--brand-ocean-700)]" />
                </div>
                <p className="text-sm font-semibold text-[var(--brand-slate-900)] mb-1">Fast Installation</p>
                <p className="text-xs text-[var(--brand-slate-600)}">{city.installTime}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ========== PROCESS ========== */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--brand-slate-900)] text-center mb-8">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <Camera className="h-7 w-7 text-[var(--brand-ocean-700)]" />
              </div>
              <div className="bg-[var(--brand-ocean-700)] text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-semibold">1</div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">Upload Photos</h3>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                Take photos of your windows and doors. Upload them through our simple online form. No in-home visit required.
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-7 w-7 text-[var(--brand-ocean-700)]" />
              </div>
              <div className="bg-[var(--brand-ocean-700)] text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-semibold">2</div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">Get 3 Options</h3>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                Receive Good, Better, and Premium pricing options within 24-48 hours. Compare tiers and choose what fits your budget.
              </p>
            </div>

            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[var(--brand-ocean-100)] flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-7 w-7 text-[var(--brand-ocean-700)]" />
              </div>
              <div className="bg-[var(--brand-ocean-700)] text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-3 text-sm font-semibold">3</div>
              <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-2">We Install</h3>
              <p className="text-sm text-[var(--brand-slate-600)] leading-relaxed">
                We handle permits, coordinate with your HOA if needed, and complete installation in 1-3 days. Code-compliant and inspected.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== AI-OPTIMIZED FAQ ========== */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight text-[var(--brand-slate-900)] text-center mb-3">
            Common Questions About Impact Windows in {city.name}
          </h2>
          <p className="text-sm text-[var(--brand-slate-600)] text-center mb-8">
            Answers to frequently asked questions about impact window installation in {city.county} County
          </p>
          <Accordion type="single" collapsible className="space-y-3">
            {cityFAQs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white rounded-xl border px-4">
                <AccordionTrigger className="font-heading font-semibold text-sm text-left text-[var(--brand-slate-900)] hover:no-underline py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[var(--brand-slate-600)] pb-4 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ========== NEARBY CITIES (INTERNAL LINKING) ========== */}
      {nearbyCities.length > 0 && (
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <h3 className="font-heading text-lg font-semibold text-[var(--brand-slate-900)] mb-4">
              Also Serving Nearby Cities
            </h3>
            <div className="flex justify-center gap-3 flex-wrap">
              {nearbyCities.map((nearbyCity, i) => (
                <Button
                  key={i}
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/impact-windows-${nearbyCity.slug}`)}
                  className="rounded-xl border-2 border-[var(--brand-ocean-600)] text-[var(--brand-ocean-700)] hover:bg-[var(--brand-ocean-50)]"
                >
                  {nearbyCity.name}
                </Button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== FINAL CTA ========== */}
      <section className="py-12 sm:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <Card className="rounded-2xl shadow-[var(--shadow-lift)] relative overflow-hidden">
            <div className="absolute inset-0" style={{background: 'radial-gradient(600px circle at 50% 0%, rgba(242,107,79,0.10), transparent 60%)'}} />
            <CardContent className="p-6 sm:p-10 text-center relative z-10">
              <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[var(--brand-slate-900)] mb-2">
                Ready to Protect Your {city.name} Home?
              </h2>
              <p className="text-sm text-[var(--brand-slate-600)] mb-6">
                Get your free quote in under 2 minutes. Upload photos, answer a few questions, compare 3 options.
              </p>
              <Button onClick={() => navigate('/get-quote')} size="lg" data-testid={`final-cta-${citySlug}`} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-14 px-8 text-base font-semibold shadow-sm w-full sm:w-auto">
                Get Your Free Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <p className="text-xs text-[var(--brand-slate-600)] mt-3">No obligation • No sales visit • Just transparent pricing</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer className="border-t bg-[var(--brand-slate-900)] text-white py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div>
              <Logo variant="dark" size="small" className="mb-3" />
              <p className="text-xs text-white/70 leading-relaxed">
                Licensed General Contractor (CGC1524851) serving {city.county} County
              </p>
            </div>
            <div>
              <p className="font-heading font-semibold text-sm text-white mb-2">Quick Links</p>
              <ul className="space-y-1 text-xs text-white/70">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="/get-quote" className="hover:text-white transition-colors">Get Quote</a></li>
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy</a></li>
                <li><a href="/terms-of-service" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>
            <div>
              <p className="font-heading font-semibold text-sm text-white mb-2">Service Areas</p>
              <p className="text-xs text-white/70">
                Miami-Dade • Broward • Palm Beach
              </p>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 text-xs text-white/50 text-center">
            <p>&copy; {new Date().getFullYear()} Snap Windows. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* ========== STICKY MOBILE CTA ========== */}
      <div className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-[hsl(var(--border))] shadow-[0_-4px_20px_rgba(15,23,32,0.12)] px-4 py-3 pb-safe">
        <Button onClick={() => navigate('/get-quote')} className="w-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] rounded-xl h-12 text-sm font-semibold shadow-sm" data-testid={`sticky-cta-${citySlug}`}>
          Get My Free Quote <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
